import React from 'react';

import { AppIcon, FeaturedIcon } from '@backrs_ui/common-ui';

import cleanUpOldPostsImage from '@backrs_ui/grow-ui/src/assets/images/online-reputation-guide/clean-up-old-posts.jpg';
import profilePhotoExampleImage from '@backrs_ui/grow-ui/src/assets/images/online-reputation-guide/profile-photo-example.jpg';
import thinkBeforeYouPostImage from '@backrs_ui/grow-ui/src/assets/images/online-reputation-guide/think-before-you-post.jpg';
import {
  GUIDE_ASIDE_WIDTH,
  GUIDE_TEXT_MAX_WIDTH,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import type {
  OnlineReputationAsideBlock,
  OnlineReputationAsideImageId,
  OnlineReputationBlock,
  OnlineReputationStep,
} from '@backrs_ui/grow-ui/src/pages/OnlineReputationGuide/content/types';

import { Box, SxProps, Theme, Typography } from '@mui/material';

type StepSectionProps = Readonly<{
  content: OnlineReputationStep;
}>;

const asideImages: Record<OnlineReputationAsideImageId, string> = {
  'clean-up-old-posts': cleanUpOldPostsImage,
  'profile-photo-example': profilePhotoExampleImage,
  'think-before-you-post': thinkBeforeYouPostImage,
};

const StepBlock: React.FC<{ block: OnlineReputationBlock }> = ({ block }) => {
  if (block.callout) {
    return (
      <Box sx={calloutSx}>
        <Typography variant="text-md-semibold" sx={calloutLabelSx}>
          {block.callout.label}
        </Typography>
        <Typography variant="text-md-regular" sx={calloutTextSx}>
          {block.callout.text}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={blockSx}>
      {block.heading && (
        <Typography variant="text-md-semibold" sx={headingSx}>
          {block.heading}
        </Typography>
      )}
      {block.text && (
        <Typography variant="text-md-regular" sx={bodySx}>
          {block.text}
        </Typography>
      )}
      {block.bullets && (
        <Box component={block.ordered ? 'ol' : 'ul'} sx={listSx}>
          {block.bullets.map((bullet) => (
            <Typography key={bullet} component="li" variant="text-md-regular" sx={bulletSx}>
              {bullet}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

const AsideChecklist: React.FC<{ block: OnlineReputationAsideBlock }> = ({ block }) => {
  if (!block.bullets) return null;

  return (
    <Box sx={block.columns === 2 ? twoColumnChecklistSx : checklistContainerSx}>
      {block.bullets.map((bullet) => (
        <Box key={bullet} sx={checklistItemSx}>
          <Box sx={checklistIconSx}>
            <AppIcon
              iconName="ArrowRight"
              diameter="16px"
              color="Colors/Foreground/fg-brand-primary"
            />
          </Box>
          <Typography variant="text-md-regular" sx={bodySx}>
            {bullet}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const AsideBlock: React.FC<{ block: OnlineReputationAsideBlock }> = ({ block }) => {
  if (block.callout) {
    return (
      <Box sx={asideCalloutSx}>
        <Typography variant="text-md-semibold" sx={headingSx}>
          {block.callout.label}
        </Typography>
        <Typography variant="text-md-regular" sx={bodySx}>
          {block.callout.text}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={asideBlockSx}>
      {block.heading && (
        <Box sx={asideHeaderSx}>
          {block.icon && (
            <FeaturedIcon iconName={block.icon} size="md" color="Colors/Text/text-primary" border />
          )}
          <Typography
            variant="text-md-semibold"
            sx={[headingSx, block.icon ? asideHeaderTitleSx : null] as SxProps<Theme>}
          >
            {block.heading}
          </Typography>
        </Box>
      )}
      {block.text && (
        <Typography variant="text-md-regular" sx={bodySx}>
          {block.text}
        </Typography>
      )}
      {block.bullets &&
        (block.checklist ? (
          <AsideChecklist block={block} />
        ) : block.ordered ? (
          <Box component="ol" sx={listSx}>
            {block.bullets.map((bullet) => (
              <Typography key={bullet} component="li" variant="text-md-regular" sx={bulletSx}>
                {bullet}
              </Typography>
            ))}
          </Box>
        ) : (
          <Box sx={asideListSx}>
            {block.bullets.map((bullet) => (
              <Typography key={bullet} variant="text-md-regular" sx={bodySx}>
                {bullet}
              </Typography>
            ))}
          </Box>
        ))}
    </Box>
  );
};

export const StepSection: React.FC<StepSectionProps> = ({ content }) => (
  <Box sx={layoutSx}>
    <Box sx={containerSx}>
      {content.subtitle && (
        <Typography variant="text-md-semibold" sx={subtitleSx}>
          {content.subtitle}
        </Typography>
      )}
      {content.intro && (
        <Typography variant="text-md-regular" sx={introSx}>
          {content.intro}
        </Typography>
      )}
      {content.blocks.map((block, index) => (
        <StepBlock key={block.heading ?? block.callout?.label ?? index} block={block} />
      ))}
    </Box>
    {content.aside && (
      <Box sx={asideCardSx}>
        {content.asideImage && (
          <Box
            component="img"
            src={asideImages[content.asideImage.id]}
            alt={content.asideImage.alt}
            sx={asideImageSx}
          />
        )}
        <Box sx={asideContentSx}>
          {content.aside.map((block, index) => (
            <AsideBlock key={block.heading ?? block.callout?.label ?? index} block={block} />
          ))}
        </Box>
      </Box>
    )}
  </Box>
);

const layoutSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-3xl'],
  '@container (min-width: 600px)': {
    flexDirection: 'row',
  },
});

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  flex: 1,
  minWidth: 0,
});

const asideCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  width: '100%',
  flexShrink: 0,
  overflow: 'hidden',
  '@container (min-width: 600px)': {
    width: GUIDE_ASIDE_WIDTH,
  },
});

const asideImageSx: SxProps<Theme> = () => ({
  display: 'block',
  width: '100%',
  aspectRatio: '16 / 9',
  objectFit: 'cover',
});

const asideContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  p: backrs.spacing['spacing-3xl'],
});

const subtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const introSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const blockSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const asideBlockSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const asideHeaderSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
});

// Vertically centers the first title line against the 40px featured icon.
const asideHeaderTitleSx: SxProps<Theme> = ({ backrs }) => ({
  pt: backrs.spacing['spacing-md'],
});

const asideListSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const checklistContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  columnGap: backrs.spacing['spacing-lg'],
  rowGap: backrs.spacing['spacing-md'],
});

const twoColumnChecklistSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  columnGap: backrs.spacing['spacing-lg'],
  rowGap: backrs.spacing['spacing-xs'],
});

const checklistItemSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-md'],
});

// Vertically centers the arrow against the first line of the item text.
const checklistIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexShrink: 0,
  pt: backrs.spacing['spacing-xs'],
});

const asideCalloutSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  p: backrs.spacing['spacing-xl'],
  borderRadius: backrs.radius['radius-lg'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
});

const headingSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const listSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  m: 0,
  pl: backrs.spacing['spacing-2xl'],
});

const bulletSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  '&::marker': {
    color: backrs.colors['Colors/Text/text-quaternary'],
  },
});

const calloutSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
  p: backrs.spacing['spacing-xl'],
  borderTopRightRadius: backrs.radius['radius-lg'],
  borderBottomRightRadius: backrs.radius['radius-lg'],
  borderLeft: `3px solid ${backrs.colors['Colors/Border/border-brand']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  maxWidth: GUIDE_TEXT_MAX_WIDTH,
});

const calloutLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-brand-primary'],
});

const calloutTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});
